// Styles
import classnames from 'classnames'
import styled from 'styled-components/macro'

// Core
import React, {Fragment, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useHistory, useLocation} from 'react-router-dom'

// Components, services, etc
import {openModal} from 'actions/modalActions'
import {createOrder} from 'actions/ordersActions'
import RequestQuote from 'assets/request_quote.svg'
import Button from 'components/Button'
import Card from 'components/Card'
import Chip from 'components/Chip'
import Icon from 'components/Icon'
import LinkButton from 'components/LinkButton'
import ProductOrders from 'components/ProductCard/ProductOrders'
import {CLICKED_LEARN_MORE} from 'constants/mixpanelConstants'
import {LOG_IN_SIGN_UP_MODAL} from 'constants/modalsConstants'
import {CDA, CLEARPROP, productDataDictionary} from 'constants/productCardConstants'
import type {ProductData} from 'constants/productCardConstants'
import {creditsSelector, propertyDataSelector} from 'selectors'
import {isInvoicePaySelector} from 'selectors/customerSelectors'
import {createSuppliedAddressObject} from 'services/addressServices'
import {getMixpanelPage, trackMixpanelEvent} from 'services/mixpanel'
import {isExisty} from 'services/utils'
import type {CreateOrderRequestBody, OpsOrderType, ProductType} from 'types/orderTypes'

// 3rd-party
import Ballot from '@material-ui/icons/Ballot'
import VerifiedUser from '@material-ui/icons/VerifiedUser'
import type {ChipProps} from 'components/Chip/Chip'
import {isAvailable} from 'services/productServices'
import {ALL_ORDERS_PATH} from 'constants/appRoutesConstants'
import {getProductNavPath} from 'services/productNavigationServices'
import type {PropertyType} from 'types/propertyTypes'
import NoPIDAlert from 'components/NoPIDAlert'

type Props = {
  isLoggedIn?: boolean
  size?: 'small' | 'large'
  orders?: Array<OpsOrderType>
  'data-sqa-id'?: string
  productType: ProductType
}

type ProductCardSmallProps = {
  productData: ProductData
  handleLearnMoreClick: () => void
}

type ProductCardLargeProps = {
  handleBuyNow: React.MouseEventHandler<HTMLButtonElement>
  productData: ProductData
  submitting: boolean
  userHasCredits: boolean
  handleLearnMoreClick: React.MouseEventHandler<HTMLButtonElement>
  isInvoicePay: boolean
  productType: ProductType
} & Pick<Props, 'isLoggedIn' | 'orders'> &
  Pick<PropertyType, 'pid'>

const ProductCard = ({isLoggedIn, productType, size, orders}: Props) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [submitting, setSubmitting] = useState(false)
  const propertyData = useSelector(propertyDataSelector)
  const productData = productDataDictionary[productType]
  const {pid} = propertyData ?? {}
  const {creditsRemaining = 0} = useSelector(creditsSelector)
  const userHasCredits = creditsRemaining > 0
  const isInvoicePay = useSelector(isInvoicePaySelector)

  const handleBuyNow = () => {
    if (isLoggedIn) {
      const suppliedAddress = createSuppliedAddressObject(propertyData)
      const placeOrder = (
        order: Omit<CreateOrderRequestBody, 'suppliedAddress'>,
        orderPrice?: string
      ) => {
        return dispatch(
          createOrder({
            orderPayload: {...order, suppliedAddress},
            handleOrder,
            handleOrderError,
            handleOrderAbandoned,
            mixpanelEventData: {
              page: getMixpanelPage(location.pathname),
              component: 'product card'
            },
            orderPrice: Number(orderPrice ?? '')
          })
        )
      }

      if (productType === CLEARPROP) {
        setSubmitting(true)
        placeOrder({pid, productType: CLEARPROP})
      } else if (productType === CDA) {
        return dispatch(
          openModal({
            modalType: 'CDA_CHECKOUT_MODAL',
            property: propertyData,
            placeOrder
          })
        )
      }
    } else {
      dispatch(
        openModal({
          modalType: LOG_IN_SIGN_UP_MODAL,
          mode: 'signUp',
          mixpanelEventData: {
            initiatedFrom: 'Product Card'
          }
        })
      )
    }
  }
  const handleOrder = (order: OpsOrderType) => {
    setSubmitting(false)

    if (order.productOrderId) {
      history.push(getProductNavPath(order))
    } else {
      history.push(ALL_ORDERS_PATH)
    }
  }
  const handleOrderError = () => {
    setSubmitting(false)
    // TODO: why submit failed go to orders?
    history.push(ALL_ORDERS_PATH)
  }

  const handleOrderAbandoned = () => setSubmitting(false)

  const handleMixpanelLearnMoreEvent = () => {
    const page = getMixpanelPage(location.pathname)
    trackMixpanelEvent(CLICKED_LEARN_MORE, {product: productType, page})
  }

  return (
    <ProductCard.Styled
      className={classnames('product-card', size)}
      $productColor={productData.productColor}
    >
      {size === 'small' ? (
        <ProductCard.Small
          productData={productData}
          handleLearnMoreClick={handleMixpanelLearnMoreEvent}
        />
      ) : (
        <ProductCard.Large
          productData={productData}
          handleBuyNow={handleBuyNow}
          isLoggedIn={isLoggedIn}
          orders={orders}
          pid={pid}
          productType={productType}
          submitting={submitting}
          userHasCredits={userHasCredits}
          handleLearnMoreClick={handleMixpanelLearnMoreEvent}
          isInvoicePay={isInvoicePay}
        />
      )}
    </ProductCard.Styled>
  )
}

function ProductIcon({icon, alt}: {icon: string; alt?: string}) {
  let productIcon = <Icon icon={icon} />
  if (icon === 'requestQuote') {
    productIcon = <img src={RequestQuote} alt={alt} />
  } else if (icon === 'ballot') {
    productIcon = <Ballot />
  } else if (icon === 'verified_user') {
    productIcon = <VerifiedUser />
  }

  return productIcon
}

ProductCard.Small = ({productData, handleLearnMoreClick}: ProductCardSmallProps) => {
  return (
    <Fragment>
      <div className='avatar'>
        <ProductIcon icon={productData.icon} alt={`${productData.title} Icon`} />
      </div>
      <h4 className='title'>{productData.title}</h4>
      <p className='description'>{productData.landingDescription}</p>
      <LinkButton
        title='Learn More →'
        variant='tertiary'
        href={productData.learnMoreLink}
        target='_blank'
        onClick={handleLearnMoreClick}
      />
    </Fragment>
  )
}

ProductCard.Large = ({
  handleBuyNow,
  isLoggedIn,
  productData,
  orders,
  pid,
  submitting,
  userHasCredits,
  handleLearnMoreClick,
  isInvoicePay,
  productType
}: ProductCardLargeProps) => {
  const shouldShowPrice =
    !isInvoicePay && (isExisty(productData.price) || isExisty(productData.minPrice))

  return (
    <Fragment>
      <div className='side left'>
        <h5 className='title'>{productData.title}</h5>
        <h3 className='subtitle'>{productData.subtitle}</h3>
        <p className='description'>
          {`${productData.popDescription} `}
          <LinkButton
            className='learn-more-button'
            title='Learn More.'
            variant='tertiary'
            href={productData.learnMoreLink}
            target='_blank'
            onClick={handleLearnMoreClick}
          />
        </p>
        {productData.isProductActive ? (
          isAvailable(productType, {pid}) ? (
            <div className='actions'>
              <Button
                variant='primary'
                title={
                  <ProductCard.ButtonLabel
                    isLoggedIn={isLoggedIn}
                    submitting={submitting}
                    productType={productType}
                    userHasCredits={userHasCredits}
                    isInvoicePay={isInvoicePay}
                  />
                }
                onClick={handleBuyNow}
                disabled={submitting}
              />
              <ProductOrders orders={orders} />
              {shouldShowPrice && (
                <p>
                  Price
                  <h5
                    className={classnames({
                      'has-credits': userHasCredits && productType === CLEARPROP
                    })}
                  >
                    {`$${productData.price || `${productData.minPrice}+`}`}
                  </h5>
                </p>
              )}
            </div>
          ) : (
            <NoPIDAlert className='not-available' />
          )
        ) : (
          <Chip color='stone' label='Coming Soon' />
        )}
      </div>
      <div className='side right'>
        <img src={productData.promoSvg} alt={`${productData.chipLabel} Product`} />
      </div>
    </Fragment>
  )
}

ProductCard.ButtonLabel = ({
  isLoggedIn,
  submitting,
  userHasCredits,
  isInvoicePay,
  productType
}: Pick<
  ProductCardLargeProps,
  'isLoggedIn' | 'submitting' | 'userHasCredits' | 'isInvoicePay' | 'productType'
>) => {
  let content
  let useCreditTip = ''
  if (productType === CLEARPROP && !isInvoicePay && userHasCredits) {
    useCreditTip = ' – 1 Credit'
  }

  const buyNowText = `Buy Now${useCreditTip}`

  if (isLoggedIn) {
    content = submitting ? (
      <span>Buying&hellip;</span>
    ) : (
      <React.Fragment>{buyNowText}</React.Fragment>
    )
  } else {
    content = <React.Fragment>Get Started</React.Fragment>
  }

  return content
}

ProductCard.defaultProps = {
  size: 'small'
}

ProductCard.Styled = styled(Card)<{$productColor: ChipProps['color']}>`
  .description {
    margin-top: 0;
    margin-bottom: 13px;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: ${({theme}) => theme.colors.grayscale.gray};
  }

  &.small {
    height: 236px;
    width: 322px;
    padding-top: 24px;
    padding-right: 16px;

    .avatar {
      height: 48px;
      width: 48px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: ${({theme, $productColor}) => theme.colors[$productColor].lightest};
      color: ${({theme, $productColor}) => theme.colors[$productColor].dark};
    }

    .title {
      margin-top: 19px;
      margin-bottom: 8px;
    }
  }

  &.large {
    width: 100%;
    padding: 0;
    display: flex;

    .side {
      flex: 1 0 auto;
      width: 50%;

      &.left {
        padding: 32px;

        .title,
        .subtitle {
          margin-bottom: 8px;
        }

        .description {
          margin-bottom: 14px;
          margin-right: 28px;
          .trimmed-content-container {
            .content-wrap {
              height: 41px;

              p {
                font-size: 0.875rem;
                line-height: 1.25rem;
                color: ${({theme}) => theme.colors.grayscale.gray};
              }
            }
          }

          .learn-more-button {
            margin-top: -3px;
            line-height: 20px;
          }
        }

        .actions {
          display: flex;
          align-items: center;
          p {
            color: ${({theme}) => theme.colors.grayscale.gray};
            margin: 0 0 0 auto;
            .has-credits {
              text-decoration: line-through;
            }
          }
          h5 {
            margin-bottom: 0;
            margin-left: 6px;
            font-weight: 400;
            display: inline;
          }
        }

        .not-available {
          margin-left: -2.5%;
          margin-bottom: 0;
          width: 105%;
        }
      }

      &.right {
        background-color: ${({theme, $productColor}) => theme.colors[$productColor].lightest};
        display: flex;
        align-items: flex-end;
        justify-content: center;
        padding: 40px 40px 0;

        img {
          width: 100%;
        }
      }
    }
  }
  @media (max-width: 1024px) {
    &.large {
      flex-direction: column-reverse;
      width: 679px;
      height: auto;
      flex: 1;
      .side {
        max-width: 100%;
        width: 100%;
      }
    }
  }
`

export default ProductCard
